import {
  Button,
  Drawer,
  Grid,
  Input,
  Message,
  RadioCard,
  TextArea,
} from "@intility/bifrost-react";
import { useState } from "react";
import {
  useChangeClientType,
  useClient,
  useUpdateClientDetails,
  useUpdateClientMembership,
} from "../api/clients";
import { IntilityIdException } from "../api/errorEnvelope";
import { usePlatforms } from "../api/platforms";
import type { AppType, ClientDetail, ClientListItem } from "../api/types";
import { APP_TYPE_CONFIG } from "../lib/clientTypes";
import { ClientStackPicker } from "./ClientStackPicker";
import { ClientTypeBadge } from "./ClientTypeBadge";

// The types an API or worker client can be switched between; a SPA is fixed.
const SWITCHABLE_TYPES: AppType[] = ["api", "worker"];

// Edits what the client object lets you change: display name, description,
// tech stack, platform and, for API and worker clients the user owns, the
// client type. The client ID and object ID are fixed once a client exists.
// One Save at the bottom of the drawer commits every change that was made.
export function ClientEditDrawer({
  client,
  open,
  onClose,
}: {
  client: ClientListItem;
  open: boolean;
  onClose: () => void;
}) {
  const { data: detail } = useClient(client.id);
  // Each time the drawer opens the form starts over from the current values:
  // the body is keyed on a counter that goes up whenever `open` turns true.
  const [session, setSession] = useState(0);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setSession((s) => s + 1);
  }

  if (!detail) return null;
  return (
    <EditDrawerBody
      key={session}
      detail={detail}
      open={open}
      onClose={onClose}
    />
  );
}

function EditDrawerBody({
  detail,
  open,
  onClose,
}: {
  detail: ClientDetail;
  open: boolean;
  onClose: () => void;
}) {
  const { data: platforms = [] } = usePlatforms();
  const updateDetails = useUpdateClientDetails(detail.id);
  const updateMembership = useUpdateClientMembership(detail.id);
  const changeType = useChangeClientType(detail.id);

  const [name, setName] = useState(detail.name);
  const [description, setDescription] = useState(detail.description ?? "");
  const [stack, setStack] = useState<string | null>(detail.stack);
  const [platformId, setPlatformId] = useState(detail.platformId ?? "");
  const [appType, setAppType] = useState<AppType>(detail.appType);
  const [error, setError] = useState<Error | null>(null);

  // Changing the type needs ownership, and only API and worker clients can.
  const canChangeType =
    detail.userRole === "owner" && SWITCHABLE_TYPES.includes(detail.appType);

  const saving =
    updateDetails.isPending ||
    updateMembership.isPending ||
    changeType.isPending;
  const trimmedName = name.trim();
  const detailsDirty =
    trimmedName !== detail.name ||
    description !== (detail.description ?? "") ||
    stack !== detail.stack;
  const membershipDirty = (platformId || null) !== (detail.platformId ?? null);
  const typeDirty = canChangeType && appType !== detail.appType;
  const canSave =
    !saving && !!trimmedName && (detailsDirty || membershipDirty || typeDirty);

  async function save() {
    if (!canSave) return;
    setError(null);
    try {
      // Only the changes that were made are sent. The details PUT is a full
      // replacement, so the stack goes with it whenever it runs (ADR-0060
      // decision 6).
      if (detailsDirty) {
        await updateDetails.mutateAsync({
          name: trimmedName,
          description,
          stack,
        });
      }
      if (membershipDirty) {
        await updateMembership.mutateAsync(platformId || null);
      }
      if (typeDirty && (appType === "api" || appType === "worker")) {
        await changeType.mutateAsync(appType);
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e : new Error("Could not save."));
    }
  }

  // The clients still holding API access, when a type change is refused.
  const holders =
    error instanceof IntilityIdException &&
    Array.isArray((error.details as { holders?: string[] } | null)?.holders)
      ? (error.details as { holders: string[] }).holders
      : [];

  return (
    <Drawer
      isOpen={open}
      onRequestClose={() => {
        if (!saving) onClose();
      }}
      header="Edit client"
      footer={
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <Button
            variant="flat"
            onClick={onClose}
            state={saving ? "inactive" : undefined}
          >
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={save}
            state={canSave ? undefined : "inactive"}
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      }
    >
      <Grid gap={24} style={{ marginTop: 24 }}>
        <Input
          label="Display name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
        <TextArea
          label="Description"
          optional
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />

        <fieldset style={{ border: "none", padding: 0, margin: 0 }}>
          <legend
            className="bf-label"
            style={{ display: "block", marginBottom: "4px" }}
          >
            Tech stack <span className="bf-small">(optional)</span>
          </legend>
          <p
            className="bf-small"
            style={{
              color: "var(--bfc-base-c-2)",
              margin: "0 0 8px",
            }}
          >
            What the client's application is built with. React and .NET get a
            setup guide that matches.
          </p>
          <ClientStackPicker
            stack={stack}
            onPick={setStack}
            disabled={saving}
          />
        </fieldset>

        <div>
          <label
            htmlFor="client-edit-platform"
            className="bf-label"
            style={{ display: "block", marginBottom: 4 }}
          >
            Platform
          </label>
          <select
            id="client-edit-platform"
            className="bf-input"
            value={platformId}
            onChange={(e) => setPlatformId(e.target.value)}
            style={{ width: "100%" }}
          >
            <option value="">Standalone (no platform)</option>
            {/* The current platform stays selectable even if the list leaves
                it out. */}
            {detail.platformId &&
              !platforms.some((p) => p.id === detail.platformId) && (
                <option value={detail.platformId}>Current platform</option>
              )}
            {platforms.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        {canChangeType && (
          <fieldset style={{ border: "none", padding: 0, margin: 0 }}>
            <legend
              className="bf-label"
              style={{ marginBottom: 8, display: "block" }}
            >
              Client type
            </legend>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {SWITCHABLE_TYPES.map((type) => (
                <RadioCard
                  key={type}
                  label={<ClientTypeBadge appType={type} />}
                  name="client-type"
                  checked={appType === type}
                  onChange={() => setAppType(type)}
                  style={{ flex: "1 1 200px" }}
                >
                  <span
                    style={{
                      fontSize: "0.875em",
                      color: "var(--bfc-base-c-2)",
                    }}
                  >
                    {APP_TYPE_CONFIG[type].description}
                  </span>
                </RadioCard>
              ))}
            </div>
            {typeDirty && (
              <Message state="neutral" style={{ marginTop: 12 }}>
                {appType === "api"
                  ? "This gives the client an inbound API surface: other clients can then be granted API access to call it."
                  : "This removes the client's inbound API surface. The change is refused while other clients hold API access to it; revoke their access first."}
              </Message>
            )}
          </fieldset>
        )}

        {error && (
          <Message state="alert" header="Could not save">
            {error.message}
            {holders.length > 0 && (
              <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
                {holders.map((h) => (
                  <li key={h}>
                    <code>{h}</code>
                  </li>
                ))}
              </ul>
            )}
          </Message>
        )}
      </Grid>
    </Drawer>
  );
}
